import {
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Textarea,
} from '@nextui-org/react';
import React from 'react';
import { FileUploader } from 'react-drag-drop-files';
import { useForm } from 'react-hook-form';

import { IconClose } from '@/assets';
import { Buttons, Images, Tag } from '@/components';
import MaxLengthInput from '@/components/max-length-input';
import { ProfileSocial } from '@/types';
import type { LinkSocialProfile } from '@/types/user';
import { getUrlSocial } from '@/utils';

import useEditCollection from './useEditColleciton';

const EditCollection: React.FC<{
  isOpen: boolean;
  onOpenChange: () => void;
  onClose: () => void;
  collectionId: number;
  thumbnail?: string;
  name: string;
  description: string;
  links?: LinkSocialProfile[];
  banner?: string;
  tags?: [
    {
      id: number;
      content: string;
    },
  ];
}> = ({
  isOpen,
  onOpenChange,
  collectionId,
  thumbnail,
  name,
  description,
  links,
  onClose,
  tags,
  banner,
}) => {
  const {
    register,
    watch,
    formState: { errors },
    handleSubmit: handleSubmitForm,
  } = useForm();
  const {
    loading,
    handleUpdateCollection,
    previewThumbnail,
    previewCover,
    handleChangeFile,
    handleRemoveFile,
    getLinks,
  } = useEditCollection();

  const nameWatch = watch('name', name);
  const descriptionWatch = watch('description', description);

  const listSocial = [
    {
      key: ProfileSocial.facebook,
      title: 'Facebook',
      placeholder: '',
    },
    {
      key: ProfileSocial.twitter,
      title: 'Twitter',
      placeholder: '',
    },
    {
      key: ProfileSocial.telegram,
      title: 'Telegram',
      placeholder: '',
    },
    {
      key: ProfileSocial.instagram,
      title: 'Instagram',
      placeholder: '',
    },
    {
      key: ProfileSocial.discord,
      title: 'Discord',
      placeholder: '',
    },
  ];

  const onSubmit = async (data: any) => {
    const links = getLinks(data);
    await handleUpdateCollection(collectionId, data.name, data.description, links, tags);
    onClose();
  };
  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      hideCloseButton
      scrollBehavior="inside"
      placement="center"
    >
      <ModalContent className="min-w-[550px] rounded-[32px] sm:min-w-[0] sm:rounded-[12px]">
        {(onClose) => (
          <>
            <ModalHeader className="flex items-center justify-between p-10 pb-8 sm:p-4 sm:pb-3">
              <span className="text-[24px] font-[600]">Edit collection details</span>
              <div
                onClick={onClose}
                className="flex h-[32px] w-[32px] cursor-pointer items-center justify-center rounded-[8px] bg-gray-100"
              >
                <IconClose />
              </div>
            </ModalHeader>
            <ModalBody className="max-h-[350px] min-h-[186px] px-[40px] sm:px-4">
              <div>
                <div className="mb-4">
                  <div className="mb-2 text-[14px] font-[600] text-black sm:text-[13px]">
                    Thumbnail
                  </div>

                  <div>
                    <div className="h-[186px] w-[40%] overflow-hidden rounded-[20px] bg-[#F0F2FA] sm:h-[90px] sm:rounded-[10px]">
                      {previewThumbnail ? (
                        <Images
                          className="h-full w-full object-cover"
                          src={previewThumbnail}
                          alt="thumbnail"
                        />
                      ) : (
                        <FileUploader
                          types={['png', 'jpg', 'jpeg']}
                          name="thumbnail"
                          classes="[&>input]:opacity-0 [&>input]:absolute sm:leading-[1] sm:-mt-[2px] cursor-pointer"
                          handleChange={(file: File) => handleChangeFile(file, 'thumbnail')}
                        >
                          <Images
                            className="h-full w-full cursor-pointer object-cover"
                            src={thumbnail}
                            alt="thumbnail"
                          />
                        </FileUploader>
                      )}
                      {!previewThumbnail ||
                        (!thumbnail && (
                          <FileUploader
                            types={['png', 'jpg', 'jpeg']}
                            name="thumbnail"
                            classes="[&>input]:opacity-0 [&>input]:absolute sm:leading-[1] sm:-mt-[2px]"
                            handleChange={(file: File) => handleChangeFile(file, 'thumbnail')}
                          >
                            <div className="opacity-1 h-[186px] w-[100%] cursor-pointer items-center pt-[47%] text-center text-[14px] text-black">
                              default thumbnail
                            </div>
                          </FileUploader>
                        ))}
                    </div>
                    <div className="mt-3 flex min-w-[400px] items-center sm:mt-2 sm:min-w-0">
                      {previewThumbnail && (
                        <span
                          onClick={() => handleRemoveFile('thumbnail')}
                          className="mr-3 cursor-pointer text-[14px] font-semibold text-gray-600 hover:opacity-70 sm:text-[12px]"
                        >
                          Remove
                        </span>
                      )}
                      <FileUploader
                        types={['png', 'jpg', 'jpeg']}
                        name="thumbnail"
                        classes="[&>input]:opacity-0 [&>input]:absolute sm:leading-[1] sm:-mt-[2px]"
                        handleChange={(file: File) => handleChangeFile(file, 'thumbnail')}
                      >
                        <span className="cursor-pointer text-[14px] font-semibold text-gray-600 hover:opacity-70 sm:text-[12px]">
                          Update
                        </span>
                      </FileUploader>
                    </div>
                  </div>
                </div>

                <div className="mb-4">
                  <div className="mb-2 text-[14px] font-[600] text-black sm:text-[13px]">
                    Wallpaper
                  </div>

                  <div>
                    <div className="h-[186px] w-full overflow-hidden rounded-[20px] bg-[#F0F2FA] sm:h-[90px] sm:rounded-[10px]">
                      {previewCover ? (
                        <Images
                          className="h-full w-full object-cover"
                          src={previewCover}
                          alt="cover"
                        />
                      ) : (
                        <FileUploader
                          types={['png', 'jpg', 'jpeg']}
                          name="cover"
                          classes="[&>input]:opacity-0 [&>input]:absolute sm:leading-[1] sm:-mt-[2px] cursor-pointer"
                          handleChange={(file: File) => handleChangeFile(file, 'cover')}
                        >
                          <Images
                            className="h-full w-full cursor-pointer object-cover"
                            src={banner}
                            alt="cover"
                          />
                        </FileUploader>
                      )}
                      {!banner ||
                        (!previewCover && (
                          <FileUploader
                            types={['png', 'jpg', 'jpeg']}
                            name="cover"
                            classes="[&>input]:opacity-0 [&>input]:absolute sm:leading-[1] sm:-mt-[2px]"
                            handleChange={(file: File) => handleChangeFile(file, 'cover')}
                          >
                            <div className="opacity-1 h-[186px] w-[100%] cursor-pointer items-center pt-[47%] text-center text-[14px] text-black">
                              default wallpaper
                            </div>
                          </FileUploader>
                        ))}
                    </div>
                    <div className="mt-3 flex min-w-[400px] items-center sm:mt-2 sm:min-w-0">
                      {previewCover && (
                        <span
                          onClick={() => handleRemoveFile('cover')}
                          className="mr-3 cursor-pointer text-[14px] font-semibold text-gray-600 hover:opacity-70 sm:text-[12px]"
                        >
                          Remove
                        </span>
                      )}
                      <FileUploader
                        types={['png', 'jpg', 'jpeg']}
                        name="cover"
                        classes="[&>input]:opacity-0 [&>input]:absolute sm:leading-[1] sm:-mt-[2px]"
                        handleChange={(file: File) => handleChangeFile(file, 'cover')}
                      >
                        <span className="cursor-pointer text-[14px] font-semibold text-gray-600 hover:opacity-70 sm:text-[12px]">
                          Update
                        </span>
                      </FileUploader>
                    </div>
                  </div>
                </div>

                <div className="mb-2">
                  <div className="mb-2 text-[14px] font-[600] text-black">
                    Collection name <span className="text-red-600">*</span>
                  </div>
                  <div>
                    <Input
                      classNames={{
                        inputWrapper: 'rounded-[100px] bg-gray-50 px-[14px]',
                      }}
                      required
                      maxLength={60}
                      placeholder="e.g. my first collection"
                      defaultValue={name}
                      {...register('name', {
                        required: 'Name is required',
                      })}
                    />
                    <div
                      className={`mt-1 flex h-[20px] ${
                        errors.name ? 'justify-between' : 'justify-end'
                      }`}
                    >
                      {errors.name && (
                        <div className="text-[13px] text-red-500">
                          {errors.name?.message as string}
                        </div>
                      )}
                      <MaxLengthInput max={60} length={nameWatch?.length || 0} />
                    </div>
                  </div>
                </div>
                <div className="mb-2">
                  <div className="mb-2 text-[14px] font-[600] text-black">Description</div>
                  <div>
                    <Textarea
                      classNames={{
                        inputWrapper: 'rounded-[16px] bg-gray-50 px-[14px] py-[2px]',
                        label: 'hidden',
                      }}
                      {...register('description')}
                      maxLength={500}
                      defaultValue={description}
                      placeholder="Write a description for your collection"
                      minRows={7}
                    />
                    <div
                      className={`mt-1 flex h-[20px] ${
                        errors.description ? 'justify-between' : 'justify-end'
                      }`}
                    >
                      {errors.description && (
                        <div className="text-[13px] text-red-500">
                          {errors.description?.message as string}
                        </div>
                      )}
                      <MaxLengthInput max={500} length={descriptionWatch?.length || 0} />
                    </div>
                  </div>
                </div>
                {tags && tags.length > 0 && (
                  <>
                    <div className="mb-2">
                      <div className="mb-2 text-[14px] font-[600] text-black sm:text-[13px]">
                        Tag <span className="text-red-600">*</span>
                        <div className="text-[13px] font-[400] text-gray-500 sm:text-[12px]">
                          Max 5 tags to be chosen and not to be changed afterward.
                        </div>
                      </div>
                      <div className="mt-4 flex flex-wrap sm:mt-5">
                        {tags?.map((tag) => <Tag key={tag.id} text={tag.content} />)}
                      </div>
                    </div>
                    <hr className="mb-5" />
                  </>
                )}
              </div>

              <div className="mb-2">
                {listSocial.map((item) => (
                  <div
                    key={item.key}
                    className="mb-[8px] text-[14px] font-[600] text-black sm:text-[13px]"
                  >
                    {item.title}
                    <div>
                      <Input
                        labelPlacement="inside"
                        startContent={
                          <div className="m-auto flex font-normal text-[#CCD1E0]">
                            <span className="pl-[6px]">https://</span>
                            <div className="ml-[6px] mr-[3px] h-auto w-[1px] bg-[#CCD1E0]" />
                          </div>
                        }
                        {...register(item.key)}
                        classNames={{
                          inputWrapper: 'rounded-[100px] bg-gray-50 sm:text-[13px] shadow-none ',
                        }}
                        placeholder={item.placeholder}
                        defaultValue={getUrlSocial(item.key, links as LinkSocialProfile[])}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </ModalBody>
            <ModalFooter className="mt-5 block p-0">
              <div className="flex items-center justify-end border-t-1 border-gray-100 px-[40px] py-[30px] sm:px-4">
                <Buttons onClick={onClose} typeBtn="cancel">
                  Cancel
                </Buttons>
                <Buttons isLoading={loading} onClick={handleSubmitForm(onSubmit)} className="ml-3">
                  Save changes
                </Buttons>
              </div>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};

export default EditCollection;
